import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Users, Clock, FileBarChart, Settings as SettingsIcon, Plus, Edit, Trash2,
  Upload, Download, X, Loader2, RefreshCw, Printer, AlertTriangle
} from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';

const fmtINR = n => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtHrs = n => `${Number(n || 0).toFixed(2)} h`;
const todayISO = () => new Date().toISOString().slice(0, 10);

// ── tiny CSV parser (no dependency) ──────────────────────────────────────────
function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter(l => l.trim());
  if (!lines.length) return [];
  const split = l => {
    const out = []; let cur = '', q = false;
    for (let i = 0; i < l.length; i++) {
      const c = l[i];
      if (c === '"') { if (q && l[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (c === ',' && !q) { out.push(cur); cur = ''; }
      else cur += c;
    }
    out.push(cur); return out.map(s => s.trim());
  };
  const headers = split(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map(l => {
    const cells = split(l); const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ''; });
    return row;
  });
}

// map loose header names → our fields
function normalizeRow(r) {
  const g = (...keys) => { for (const k of keys) if (r[k] != null && r[k] !== '') return r[k]; return ''; };
  return {
    emp_code: g('emp_code', 'employee_code', 'code', 'emp.code', 'empcode'),
    name: g('name', 'employee_name', 'employee'),
    department: g('department', 'dept'),
    designation: g('designation', 'role', 'title'),
    hourly_rate: g('hourly_rate', 'normal_rate', 'rate', 'normal_hourly_rate'),
    overtime_rate: g('overtime_rate', 'ot_rate', 'overtime_hourly_rate'),
  };
}

export default function PayrollPage() {
  const { canWrite, canDelete } = usePermission('payroll');
  const { user } = useAuth();
  const { settings: appSettings } = useSettings() || {};
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empModal, setEmpModal] = useState(null);
  const [timeModal, setTimeModal] = useState(null);
  const [importState, setImportState] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p] = await Promise.all([
        api.get('/payroll/employees?all=1'),
        api.get('/projects').catch(() => ({ data: [] })),
      ]);
      setEmployees(e.data);
      setProjects(Array.isArray(p.data) ? p.data : (p.data?.rows || p.data?.projects || []));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load payroll');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const saveEmployee = async (form) => {
    setSaving(true);
    try {
      if (empModal.row) await api.put(`/payroll/employees/${empModal.row.id}`, form);
      else await api.post('/payroll/employees', form);
      toast.success('Saved'); setEmpModal(null); loadEmployees();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const delEmployee = async (emp) => {
    if (!window.confirm(`Delete employee ${emp.name}? (Kept inactive if they have time entries)`)) return;
    try { const r = await api.delete(`/payroll/employees/${emp.id}`); toast.success(r.data.message); loadEmployees(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  // ── import ─────────────────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const csv = 'emp_code,name,department,designation,hourly_rate,overtime_rate\nEMP001,Ramesh Kumar,Carpentry,Carpenter,120,180\nEMP002,Suresh Yadav,Polish,Polisher,110,165\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = 'payroll_employee_template.csv'; a.click();
    URL.revokeObjectURL(a.href);
  };

  const loadSheetJS = () => new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    s.onload = () => resolve(window.XLSX);
    s.onerror = () => reject(new Error('Could not load Excel parser — please use CSV'));
    document.head.appendChild(s);
  });

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    try {
      let rawRows = [];
      if (file.name.toLowerCase().endsWith('.csv')) {
        rawRows = parseCSV(await file.text());
      } else {
        const XLSX = await loadSheetJS();
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
        rawRows = json.map(r => {
          const low = {}; Object.keys(r).forEach(k => { low[k.toLowerCase().replace(/\s+/g, '_')] = r[k]; });
          return low;
        });
      }
      const rows = rawRows.map(normalizeRow).filter(r => r.emp_code || r.name);
      if (!rows.length) return toast.error('No rows found in file');
      setImportState({ rows, result: null });
    } catch (err) {
      toast.error(err.message || 'Could not read file');
    }
  };

  const confirmImport = async () => {
    setSaving(true);
    try {
      const res = await api.post('/payroll/employees/import', { rows: importState.rows });
      setImportState(s => ({ ...s, result: res.data }));
      toast.success(res.data.message);
      loadEmployees();
    } catch (err) { toast.error(err.response?.data?.message || 'Import failed'); }
    finally { setSaving(false); }
  };

  const TabBtn = ({ id, icon: Icon, label, adminOnly: ao }) => (ao && !isAdmin) ? null : (
    <button onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
        tab === id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFileChosen} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" /> Payroll
          </h1>
          <p className="text-sm text-gray-500">Employees, time entry &amp; overtime — separate from the Worker system</p>
        </div>
        <button onClick={loadEmployees} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn id="employees" icon={Users} label={`Employees (${employees.length})`} />
        <TabBtn id="time" icon={Clock} label="Time Entry" />
        <TabBtn id="report" icon={FileBarChart} label="Payroll Report" adminOnly />
        <TabBtn id="settings" icon={SettingsIcon} label="OT Settings" adminOnly />
      </div>

      {tab === 'employees' && (
        <EmployeesTab
          employees={employees} isAdmin={isAdmin} canWrite={canWrite} canDelete={canDelete}
          onAdd={() => setEmpModal({})} onEdit={r => setEmpModal({ row: r })} onDelete={delEmployee}
          onImport={() => fileRef.current?.click()} onTemplate={downloadTemplate} />
      )}
      {tab === 'time' && (
        <TimeEntryTab employees={employees.filter(e => e.is_active)} projects={projects}
          isAdmin={isAdmin} canWrite={canWrite} canDelete={canDelete}
          onAdd={() => setTimeModal({})} />
      )}
      {tab === 'report' && isAdmin && (
        <ReportTab employees={employees} companyName={appSettings?.company_name} companyAddress={appSettings?.company_address} />
      )}
      {tab === 'settings' && isAdmin && <OTSettingsTab />}

      {empModal && (
        <EmployeeModal modal={empModal} isAdmin={isAdmin} saving={saving}
          onClose={() => setEmpModal(null)} onSave={saveEmployee} />
      )}
      {timeModal && (
        <TimeEntryModal employees={employees.filter(e => e.is_active)} projects={projects} saving={saving}
          onClose={() => setTimeModal(null)}
          onSaved={() => { setTimeModal(null); setTab('time'); }} />
      )}
      {importState && (
        <ImportModal state={importState} saving={saving}
          onClose={() => setImportState(null)} onConfirm={confirmImport} />
      )}
    </div>
  );
}

// ══ EMPLOYEES ═════════════════════════════════════════════════════════════════
function EmployeesTab({ employees, isAdmin, canWrite, canDelete, onAdd, onEdit, onDelete, onImport, onTemplate }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
      <div className="flex flex-wrap justify-between items-center gap-2 p-3 border-b border-gray-100">
        <span className="text-sm font-bold text-gray-700">Employee Master</span>
        {canWrite && (
          <div className="flex gap-2">
            <button onClick={onTemplate} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">
              <Download className="w-3.5 h-3.5" /> Template
            </button>
            <button onClick={onImport} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold">
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
            <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold">
              <Plus className="w-3.5 h-3.5" /> Add Employee
            </button>
          </div>
        )}
      </div>
      <table className="w-full text-sm min-w-[720px]">
        <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <th className="px-4 py-3">Emp Code</th><th className="px-4 py-3">Name</th>
          <th className="px-4 py-3">Department</th><th className="px-4 py-3">Designation</th>
          {isAdmin && <th className="px-4 py-3 text-right">Normal Rate</th>}
          {isAdmin && <th className="px-4 py-3 text-right">OT Rate</th>}
          <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
        </tr></thead>
        <tbody className="divide-y divide-gray-100">
          {employees.map(e => (
            <tr key={e.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-semibold text-blue-700">{e.emp_code}</td>
              <td className="px-4 py-3 font-medium">{e.name}</td>
              <td className="px-4 py-3 text-gray-600">{e.department || '—'}</td>
              <td className="px-4 py-3 text-gray-600">{e.designation || '—'}</td>
              {isAdmin && <td className="px-4 py-3 text-right">{fmtINR(e.hourly_rate)}</td>}
              {isAdmin && <td className="px-4 py-3 text-right">{fmtINR(e.overtime_rate)}</td>}
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${e.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {e.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-1.5">
                  {canWrite && <button onClick={() => onEdit(e)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>}
                  {canDelete && <button onClick={() => onDelete(e)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </td>
            </tr>
          ))}
          {!employees.length && <tr><td colSpan={isAdmin ? 8 : 6} className="p-8 text-center text-gray-400 text-sm">No employees. Add or import to begin.</td></tr>}
        </tbody>
      </table>
      {!isAdmin && <p className="p-3 text-xs text-gray-400 border-t border-gray-100">Rates and amounts are visible to admins only.</p>}
    </div>
  );
}

function EmployeeModal({ modal, isAdmin, saving, onClose, onSave }) {
  const r = modal.row;
  const [f, setF] = useState({
    emp_code: r?.emp_code || '', name: r?.name || '', department: r?.department || '',
    designation: r?.designation || '', hourly_rate: r?.hourly_rate ?? '', overtime_rate: r?.overtime_rate ?? '',
    is_active: r ? r.is_active : 1,
  });
  const set = p => setF(s => ({ ...s, ...p }));
  return (
    <Modal title={r ? 'Edit Employee' : 'Add Employee'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <F label="Emp Code *"><input className="minp" value={f.emp_code} onChange={e => set({ emp_code: e.target.value })} /></F>
        <F label="Name *"><input className="minp" value={f.name} onChange={e => set({ name: e.target.value })} /></F>
        <F label="Department"><input className="minp" value={f.department} onChange={e => set({ department: e.target.value })} /></F>
        <F label="Designation"><input className="minp" value={f.designation} onChange={e => set({ designation: e.target.value })} /></F>
        {isAdmin && <F label="Normal Hourly Rate (₹)"><input className="minp" type="number" min="0" step="0.01" value={f.hourly_rate} onChange={e => set({ hourly_rate: e.target.value })} /></F>}
        {isAdmin && <F label="Overtime Hourly Rate (₹)"><input className="minp" type="number" min="0" step="0.01" value={f.overtime_rate} onChange={e => set({ overtime_rate: e.target.value })} /></F>}
      </div>
      {r && (
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input type="checkbox" checked={!!f.is_active} onChange={e => set({ is_active: e.target.checked ? 1 : 0 })} /> Active
        </label>
      )}
      <ModalActions saving={saving} onClose={onClose} onSave={() => onSave(f)} />
    </Modal>
  );
}

// ══ TIME ENTRY ════════════════════════════════════════════════════════════════
function TimeEntryTab({ employees, projects, isAdmin, canWrite, canDelete, onAdd }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ from: todayISO(), to: todayISO(), employee_id: '' });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.employee_id) params.employee_id = filters.employee_id;
      const res = await api.get('/payroll/time-entries', { params });
      setRows(res.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    if (!window.confirm('Delete this time entry?')) return;
    try { await api.delete(`/payroll/time-entries/${row.id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-end gap-3">
        <F label="From"><input className="minp" type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} /></F>
        <F label="To"><input className="minp" type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} /></F>
        <F label="Employee">
          <select className="minp" value={filters.employee_id} onChange={e => setFilters(f => ({ ...f, employee_id: e.target.value }))}>
            <option value="">All</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.emp_code})</option>)}
          </select>
        </F>
        <div className="flex-1" />
        {canWrite && (
          <button onClick={() => setModal({})} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> Add Time Entry
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <th className="px-4 py-3">Date</th><th className="px-4 py-3">Employee</th>
            <th className="px-4 py-3">Project</th><th className="px-4 py-3">Proto</th>
            <th className="px-4 py-3">In</th><th className="px-4 py-3">Out</th>
            <th className="px-4 py-3 text-right">Normal</th><th className="px-4 py-3 text-right">OT</th>
            <th className="px-4 py-3 text-right">Total</th>
            {isAdmin && <th className="px-4 py-3 text-right">Amount</th>}
            <th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">{new Date(r.entry_date).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-3 font-medium">{r.employee_name}<span className="text-gray-400 text-xs"> ({r.emp_code})</span></td>
                <td className="px-4 py-3 text-gray-600">{r.project_code || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{r.proto_code || '—'}</td>
                <td className="px-4 py-3">{String(r.in_time).slice(0, 5)}</td>
                <td className="px-4 py-3">{String(r.out_time).slice(0, 5)}</td>
                <td className="px-4 py-3 text-right">{fmtHrs(r.normal_hours)}</td>
                <td className="px-4 py-3 text-right text-amber-600 font-semibold">{fmtHrs(r.ot_hours)}</td>
                <td className="px-4 py-3 text-right font-semibold">{fmtHrs(r.total_hours)}</td>
                {isAdmin && <td className="px-4 py-3 text-right font-bold">{fmtINR(r.amount)}</td>}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    {canWrite && <button onClick={() => setModal({ row: r })} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>}
                    {canDelete && <button onClick={() => del(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={isAdmin ? 11 : 10} className="p-8 text-center text-gray-400 text-sm">{loading ? 'Loading…' : 'No time entries for this range'}</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <TimeEntryModal employees={employees} projects={projects} row={modal.row} saving={false}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function TimeEntryModal({ employees, projects, row, onClose, onSaved }) {
  const [f, setF] = useState({
    employee_id: row?.employee_id || '', project_id: row?.project_id || '', proto_code: row?.proto_code || '',
    entry_date: row?.entry_date ? String(row.entry_date).slice(0, 10) : todayISO(),
    in_time: row ? String(row.in_time).slice(0, 5) : '09:00',
    out_time: row ? String(row.out_time).slice(0, 5) : '18:00',
  });
  const [saving, setSaving] = useState(false);
  const set = p => setF(s => ({ ...s, ...p }));
  const save = async () => {
    setSaving(true);
    try {
      if (row) await api.put(`/payroll/time-entries/${row.id}`, f);
      else await api.post('/payroll/time-entries', f);
      toast.success('Saved'); onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={row ? 'Edit Time Entry' : 'Add Time Entry'} onClose={onClose}>
      <div className="space-y-3">
        <F label="Employee *">
          <select className="minp" value={f.employee_id} onChange={e => set({ employee_id: e.target.value })} disabled={!!row}>
            <option value="">— Select —</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.emp_code})</option>)}
          </select>
        </F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Project">
            <ProjectSelect projects={projects} value={f.project_id} onChange={v => set({ project_id: v })}
              placeholder="—" className="minp" />
          </F>
          <F label="Proto Code"><input className="minp" value={f.proto_code} onChange={e => set({ proto_code: e.target.value })} /></F>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Date *"><input className="minp" type="date" value={f.entry_date} onChange={e => set({ entry_date: e.target.value })} /></F>
          <F label="In (24h) *"><input className="minp" type="time" value={f.in_time} onChange={e => set({ in_time: e.target.value })} /></F>
          <F label="Out (24h) *"><input className="minp" type="time" value={f.out_time} onChange={e => set({ out_time: e.target.value })} /></F>
        </div>
        <p className="text-xs text-gray-400">Back-to-back entries allowed; overlaps rejected. Normal vs OT split by the configured OT window. No overnight shifts.</p>
      </div>
      <ModalActions saving={saving} onClose={onClose} onSave={save} />
    </Modal>
  );
}

// ══ REPORT (admin) ════════════════════════════════════════════════════════════
function ReportTab({ employees, companyName, companyAddress }) {
  const [range, setRange] = useState({ from: todayISO(), to: todayISO(), employee_id: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/report', { params: range });
      setData(res.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Report failed'); }
    finally { setLoading(false); }
  };

  const printReport = () => {
    if (!data) return;
    const rowsHtml = data.report.map(r => `
      <tr><td>${r.emp_code}</td><td>${r.employee_name}</td><td>${r.department || '—'}</td>
      <td class="r">${r.normal_hours.toFixed(2)}</td><td class="r">${r.ot_hours.toFixed(2)}</td>
      <td class="r">${fmtINR(r.hourly_rate)}</td><td class="r">${fmtINR(r.overtime_rate)}</td>
      <td class="r"><strong>${fmtINR(r.total_amount)}</strong></td></tr>`).join('');
    const html = `<html><head><title>Payroll Report</title><style>
      *{margin:0;padding:0;box-sizing:border-box;} body{font-family:Arial;font-size:12px;padding:20px;}
      .h{text-align:center;border-bottom:3px solid #1a56db;padding-bottom:10px;margin-bottom:14px;}
      .cn{font-size:20px;font-weight:800;color:#1a56db;} .ca{font-size:11px;color:#555;}
      .rng{font-size:13px;margin:8px 0;font-weight:700;}
      table{width:100%;border-collapse:collapse;} th{background:#1a56db;color:#fff;padding:7px;font-size:10px;text-transform:uppercase;text-align:left;}
      td{padding:6px 7px;border-bottom:1px solid #e5e7eb;} .r{text-align:right;}
      tfoot td{border-top:2px solid #1a56db;font-weight:800;background:#eff6ff;}
      @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact;}}
    </style></head><body>
      <div class="h"><div class="cn">${companyName || 'MOZI INNOVATORS LLP'}</div><div class="ca">${companyAddress || ''}</div></div>
      <div class="rng">Payroll Report: ${new Date(data.from).toLocaleDateString('en-IN')} to ${new Date(data.to).toLocaleDateString('en-IN')}</div>
      <table><thead><tr><th>Code</th><th>Name</th><th>Dept</th><th class="r">Normal Hrs</th><th class="r">OT Hrs</th><th class="r">Rate</th><th class="r">OT Rate</th><th class="r">Amount</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot><tr><td colspan="3">GRAND TOTAL</td><td class="r">${data.grand_total.normal_hours.toFixed(2)}</td><td class="r">${data.grand_total.ot_hours.toFixed(2)}</td><td></td><td></td><td class="r">${fmtINR(data.grand_total.total_amount)}</td></tr></tfoot>
      </table></body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-end gap-3">
        <F label="From"><input className="minp" type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} /></F>
        <F label="To"><input className="minp" type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} /></F>
        <F label="Employee">
          <select className="minp" value={range.employee_id} onChange={e => setRange(r => ({ ...r, employee_id: e.target.value }))}>
            <option value="">All</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.emp_code})</option>)}
          </select>
        </F>
        <button onClick={run} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold">
          {loading ? 'Running…' : 'Run Report'}
        </button>
        {data && <button onClick={printReport} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700"><Printer className="w-4 h-4" /> Print</button>}
      </div>

      {data && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Dept</th>
              <th className="px-4 py-3 text-right">Normal Hrs</th><th className="px-4 py-3 text-right">OT Hrs</th>
              <th className="px-4 py-3 text-right">Normal Amt</th><th className="px-4 py-3 text-right">OT Amt</th>
              <th className="px-4 py-3 text-right">Total</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {data.report.map(r => (
                <tr key={r.employee_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-blue-700">{r.emp_code}</td>
                  <td className="px-4 py-3 font-medium">{r.employee_name}</td>
                  <td className="px-4 py-3 text-gray-600">{r.department || '—'}</td>
                  <td className="px-4 py-3 text-right">{r.normal_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-amber-600">{r.ot_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{fmtINR(r.normal_amount)}</td>
                  <td className="px-4 py-3 text-right">{fmtINR(r.ot_amount)}</td>
                  <td className="px-4 py-3 text-right font-bold">{fmtINR(r.total_amount)}</td>
                </tr>
              ))}
              {!data.report.length && <tr><td colSpan={8} className="p-8 text-center text-gray-400 text-sm">No time entries in this range</td></tr>}
            </tbody>
            {data.report.length > 0 && (
              <tfoot><tr className="bg-blue-50 font-bold">
                <td className="px-4 py-3" colSpan={3}>Grand Total</td>
                <td className="px-4 py-3 text-right">{data.grand_total.normal_hours.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">{data.grand_total.ot_hours.toFixed(2)}</td>
                <td colSpan={2}></td>
                <td className="px-4 py-3 text-right">{fmtINR(data.grand_total.total_amount)}</td>
              </tr></tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ══ OT SETTINGS (admin) ═══════════════════════════════════════════════════════
function OTSettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/payroll/settings').then(r => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put('/payroll/settings', s); setS(r.data.settings); toast.success('Saved'); }
    catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="p-6 text-gray-400 text-sm">Loading…</div>;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 max-w-lg space-y-4">
      <h2 className="text-sm font-bold text-gray-700 uppercase">Working-Time Window</h2>
      <div className="grid grid-cols-2 gap-3">
        <F label="Normal Start"><input className="minp" type="time" value={s.normal_start} onChange={e => setS(v => ({ ...v, normal_start: e.target.value }))} /></F>
        <F label="Normal End"><input className="minp" type="time" value={s.normal_end} onChange={e => setS(v => ({ ...v, normal_end: e.target.value }))} /></F>
        <F label="OT Start"><input className="minp" type="time" value={s.ot_start} onChange={e => setS(v => ({ ...v, ot_start: e.target.value }))} /></F>
        <F label="OT End"><input className="minp" type="time" value={s.ot_end} onChange={e => setS(v => ({ ...v, ot_end: e.target.value }))} /></F>
      </div>
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        Hours inside the OT window count as overtime; the rest counts as normal. Applies to new/edited time entries.
      </div>
      <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
}

// ══ IMPORT MODAL ══════════════════════════════════════════════════════════════
function ImportModal({ state, saving, onClose, onConfirm }) {
  const { rows, result } = state;
  return (
    <Modal title="Import Employees" onClose={onClose} wide>
      {!result ? (
        <>
          <p className="text-sm text-gray-600 mb-2">{rows.length} row(s) parsed. Review, then import. Existing/duplicate codes are skipped automatically.</p>
          <div className="max-h-72 overflow-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0"><tr className="text-left text-gray-500">
                <th className="px-2 py-1.5">Code</th><th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Dept</th>
                <th className="px-2 py-1.5">Designation</th><th className="px-2 py-1.5 text-right">Rate</th><th className="px-2 py-1.5 text-right">OT</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => (
                  <tr key={i} className={!r.emp_code || !r.name ? 'bg-red-50' : ''}>
                    <td className="px-2 py-1.5 font-semibold">{r.emp_code || <span className="text-red-500">missing</span>}</td>
                    <td className="px-2 py-1.5">{r.name || <span className="text-red-500">missing</span>}</td>
                    <td className="px-2 py-1.5">{r.department}</td><td className="px-2 py-1.5">{r.designation}</td>
                    <td className="px-2 py-1.5 text-right">{r.hourly_rate}</td><td className="px-2 py-1.5 text-right">{r.overtime_rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ModalActions saving={saving} onClose={onClose} onSave={onConfirm} saveLabel={`Import ${rows.length}`} />
        </>
      ) : (
        <>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 mb-3">
            Imported <strong>{result.imported}</strong> employee(s). Skipped {result.skipped}.
          </div>
          {result.errors?.length > 0 && (
            <div className="max-h-52 overflow-auto border border-amber-200 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
              {result.errors.map((e, i) => <div key={i}>Row {e.line}: {e.error}</div>)}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold">Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ══ shared UI ═════════════════════════════════════════════════════════════════
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl p-5 w-full ${wide ? 'max-w-2xl' : 'max-w-md'} space-y-3`}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{title}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        {children}
      </div>
      <style>{`.minp{width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;} .minp:focus{outline:2px solid #bfdbfe;}`}</style>
    </div>
  );
}
function ModalActions({ saving, onClose, onSave, saveLabel = 'Save' }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
      <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
        {saving ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}
function F({ label, children }) {
  return <div><label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>{children}</div>;
}
